"use client";

import { useState, useCallback, useEffect } from "react";
import type { AnalysisResult } from "@/lib/music/analyzer-types";

// ── Types ──────────────────────────────────────────────────────

export interface GenerationHistoryItem {
  id: string;
  prompt: string;
  bpm: number;
  measures: number;
  result: AnalysisResult;
  generationTimeMs?: number;
  timestamp: number;
}

// ── Constants ──────────────────────────────────────────────────

const STORAGE_KEY = "virtuosa:generation-history";
const MAX_ITEMS = 20;

// ── Helpers ────────────────────────────────────────────────────

function loadItems(): GenerationHistoryItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveItems(items: GenerationHistoryItem[]): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // localStorage full or unavailable, silently fail
  }
}

function generateId(): string {
  return `gen_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// ── Hook ───────────────────────────────────────────────────────

export function useGenerationHistory() {
  const [items, setItems] = useState<GenerationHistoryItem[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    setItems(loadItems());
  }, []);

  const add = useCallback(
    (entry: {
      prompt: string;
      bpm: number;
      measures: number;
      result: AnalysisResult;
      generationTimeMs?: number;
    }) => {
      setItems((prev) => {
        const item: GenerationHistoryItem = {
          id: generateId(),
          ...entry,
          timestamp: Date.now(),
        };
        const next = [item, ...prev].slice(0, MAX_ITEMS);
        saveItems(next);
        return next;
      });
    },
    [],
  );

  const remove = useCallback((id: string) => {
    setItems((prev) => {
      const next = prev.filter((item) => item.id !== id);
      saveItems(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    saveItems([]);
  }, []);

  return { items, add, remove, clear };
}
